/**
 * DOM 非依存の Excel 風グリッド選択・TSV 変換・矢印移動・貼付先算出。
 * KomaGrid (需要想定) / Supply48DataGrid (48点データ) 両 editor が共有する。
 */
export type CellPos = { row: number; col: number }
export type CellRange = {
  rowMin: number
  rowMax: number
  colMin: number
  colMax: number
}

/** グリッドの行数・列数 (境界判定・貼付先の clamp に使う)。 */
export type GridBounds = { rowCount: number; colCount: number }

export function makeRange(anchor: CellPos, focus: CellPos): CellRange {
  return {
    rowMin: Math.min(anchor.row, focus.row),
    rowMax: Math.max(anchor.row, focus.row),
    colMin: Math.min(anchor.col, focus.col),
    colMax: Math.max(anchor.col, focus.col),
  }
}

export function isInRange(
  range: CellRange | null,
  row: number,
  col: number,
): boolean {
  return (
    range !== null &&
    row >= range.rowMin &&
    row <= range.rowMax &&
    col >= range.colMin &&
    col <= range.colMax
  )
}

export function isMultiCellRange(range: CellRange | null): boolean {
  return (
    range !== null &&
    (range.rowMax > range.rowMin || range.colMax > range.colMin)
  )
}

export function rangeCells(range: CellRange): CellPos[] {
  const cells: CellPos[] = []
  for (let row = range.rowMin; row <= range.rowMax; row++) {
    for (let col = range.colMin; col <= range.colMax; col++)
      cells.push({ row, col })
  }
  return cells
}

export function parseTsv(text: string): string[][] {
  return text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\n+$/, '')
    .split('\n')
    .map((line) => line.split('\t'))
}

export function buildTsv(rows: string[][]): string {
  return rows.map((cols) => cols.join('\t')).join('\n')
}

/**
 * 矢印キー 1 歩ぶんの移動先。グリッド外に出る場合は `null` (端で止まる)。
 * 「端では同じセルに留まる」挙動が欲しい caller は `stepCell(...) ?? pos` する。
 */
export function stepCell(
  pos: CellPos,
  dr: number,
  dc: number,
  bounds: GridBounds,
): CellPos | null {
  const next = { row: pos.row + dr, col: pos.col + dc }
  if (
    next.row < 0 ||
    next.row >= bounds.rowCount ||
    next.col < 0 ||
    next.col >= bounds.colCount
  ) {
    return null
  }
  return next
}

/** 範囲を row-major に走査し、cellText で文字列化した値を TSV へ変換する。 */
export function rangeToTsv(
  range: CellRange,
  cellText: (row: number, col: number) => string,
): string {
  const rows: string[][] = []
  for (let row = range.rowMin; row <= range.rowMax; row++) {
    const cols: string[] = []
    for (let col = range.colMin; col <= range.colMax; col++)
      cols.push(cellText(row, col))
    rows.push(cols)
  }
  return buildTsv(rows)
}

/**
 * セル文字列を数値にする (trim → 桁区切り ',' 除去 → Number())。
 * 空文字 / 数値化できない文字列は undefined。
 */
export function parseCellNumber(raw: string): number | undefined {
  const trimmed = raw.trim()
  if (trimmed === '') return undefined
  const n = Number(trimmed.replace(/,/g, ''))
  return Number.isNaN(n) ? undefined : n
}

export type PastePlan = {
  /** 貼付元が 1 値 (1 行 1 列) か。caller が native paste への委譲等を判断する材料 */
  isSingleValue: boolean
  /** 1 値を複数セル選択全体へ展開する貼付か (Excel の「1 値を選択範囲へ複製」相当) */
  fillWholeSelection: boolean
  /**
   * 実際に書き込み対象となる矩形。fillWholeSelection の時は選択範囲そのもの
   * (caller が選択範囲をグリッド内に保つ前提)、それ以外はグリッド境界で clamp 済み
   */
  targetRange: CellRange
  /** targetRange 内の (row, col) に対応する貼付元テキスト。貼付元に無いセル (ragged 行) は undefined */
  sourceAt: (row: number, col: number) => string | undefined
}

/**
 * parseTsv 済み matrix と貼付起点・選択範囲・グリッド境界から、貼付先矩形と
 * 貼付元セルの対応 (PastePlan) を決める。
 *
 * 貼付先矩形はグリッド内に収まる行だけから決める (下端で切り捨てられる行は
 * 幅にも影響しない)。Excel で列全体 (100 万行) をコピーした状態で貼られても、
 * グリッド外の行を走査したり spread に載せたりしない。空の matrix は起点
 * 1 セルの矩形 (sourceAt は常に undefined) として扱う。
 */
export function planPaste(
  matrix: string[][],
  start: CellPos,
  selection: CellRange | null,
  bounds: GridBounds,
): PastePlan {
  const isSingleValue = matrix.length === 1 && matrix[0]?.length === 1

  if (isSingleValue && selection !== null && isMultiCellRange(selection)) {
    return {
      isSingleValue,
      fillWholeSelection: true,
      targetRange: selection,
      sourceAt: () => matrix[0]?.[0],
    }
  }

  const rows = matrix.slice(0, Math.max(0, bounds.rowCount - start.row))
  let colSpan = 1
  for (const line of rows) colSpan = Math.max(colSpan, line.length)
  const rowSpan = Math.max(1, rows.length)
  const targetRange = makeRange(start, {
    row: Math.min(bounds.rowCount - 1, start.row + rowSpan - 1),
    col: Math.min(bounds.colCount - 1, start.col + colSpan - 1),
  })
  return {
    isSingleValue,
    fillWholeSelection: false,
    targetRange,
    sourceAt: (row, col) => matrix[row - start.row]?.[col - start.col],
  }
}
